class PremiumMakingProcess extends HTMLElement {
  connectedCallback() {
    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      this.initialize();
      return;
    }
    this.addEventListener('focusin', this.initialize);
    this.initializationObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.initialize();
    }, { rootMargin: '600px 0px' });
    this.initializationObserver.observe(this);
  }

  initialize = () => {
    if (this.initialized || !this.isConnected) return;
    this.initialized = true;
    this.initializationObserver?.disconnect();
    this.removeEventListener('focusin', this.initialize);
    this.classList.add('is-reveal-ready');
    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.carouselStates = Array.from(this.querySelectorAll('[data-pmp-carousel]')).map((track) =>
      this.setupCarousel(track)
    );

    this.mobileQuery.addEventListener('change', this.onViewportChange);
    this.reducedMotionQuery.addEventListener('change', this.onViewportChange);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.setupConnections();
    this.setupObserver();
  };

  disconnectedCallback() {
    this.initializationObserver?.disconnect();
    this.removeEventListener('focusin', this.initialize);
    this.initialized = false;
    this.mobileQuery?.removeEventListener('change', this.onViewportChange);
    this.reducedMotionQuery?.removeEventListener('change', this.onViewportChange);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    window.removeEventListener('resize', this.queueLayout);
    this.connectionObserver?.disconnect();
    cancelAnimationFrame(this.connectionFrame);
    this.observer?.disconnect();
    this.carouselStates?.forEach((state) => this.destroyCarousel(state));
  }

  setupCarousel(track) {
    const name = track.dataset.pmpCarousel;
    const state = {
      track,
      slides: Array.from(track.querySelectorAll('[data-pmp-slide]')),
      dots: Array.from(this.querySelectorAll(`[data-pmp-dot-for="${name}"]`)),
      interval: Number(track.dataset.interval) || 4000,
      index: 0,
    };

    state.onPointerDown = () => this.stopCarousel(state);
    state.onPointerUp = () => {
      this.updateCarouselIndex(state);
      this.restartCarousel(state);
    };
    state.onScroll = () => {
      clearTimeout(state.scrollTimer);
      state.scrollTimer = setTimeout(() => this.updateCarouselIndex(state), 100);
    };
    state.onDotClick = (event) => {
      const dot = event.target.closest('[data-pmp-dot-index]');
      if (!dot) return;
      this.goToSlide(state, Number(dot.dataset.pmpDotIndex));
      this.restartCarousel(state);
    };

    track.addEventListener('pointerdown', state.onPointerDown);
    track.addEventListener('pointerup', state.onPointerUp);
    track.addEventListener('pointercancel', state.onPointerUp);
    track.addEventListener('scroll', state.onScroll, { passive: true });
    state.dots.forEach((dot) => dot.addEventListener('click', state.onDotClick));
    this.updateDots(state);
    return state;
  }

  destroyCarousel(state) {
    this.stopCarousel(state);
    clearTimeout(state.restartTimer);
    clearTimeout(state.scrollTimer);
    state.track.removeEventListener('pointerdown', state.onPointerDown);
    state.track.removeEventListener('pointerup', state.onPointerUp);
    state.track.removeEventListener('pointercancel', state.onPointerUp);
    state.track.removeEventListener('scroll', state.onScroll);
    state.dots.forEach((dot) => dot.removeEventListener('click', state.onDotClick));
  }

  startCarousel(state) {
    this.stopCarousel(state);
    if (
      !this.isInView ||
      document.hidden ||
      !this.mobileQuery.matches ||
      this.reducedMotionQuery.matches ||
      state.slides.length < 2
    ) return;

    state.timer = setInterval(() => this.goToSlide(state, (state.index + 1) % state.slides.length), state.interval);
  }

  stopCarousel(state) {
    clearInterval(state.timer);
    state.timer = undefined;
  }

  restartCarousel(state) {
    this.stopCarousel(state);
    clearTimeout(state.restartTimer);
    state.restartTimer = setTimeout(() => this.startCarousel(state), state.interval);
  }

  goToSlide(state, index, behavior = 'smooth') {
    const target = state.slides[index];
    if (!target) return;
    state.index = index;
    const firstOffset = state.slides[0].offsetLeft;
    state.track.scrollTo({ left: target.offsetLeft - firstOffset, behavior });
    this.updateDots(state);
  }

  updateCarouselIndex(state) {
    if (state.slides.length === 0) return;
    const firstOffset = state.slides[0].offsetLeft;
    const currentPosition = state.track.scrollLeft + firstOffset;
    let closestDistance = Number.POSITIVE_INFINITY;

    state.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - currentPosition);
      if (distance >= closestDistance) return;
      closestDistance = distance;
      state.index = index;
    });
    this.updateDots(state);
  }

  updateDots(state) {
    state.slides.forEach((slide, index) => slide.classList.toggle('is-active', index === state.index));
    state.dots.forEach((dot, index) => {
      const active = index === state.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  onViewportChange = () => {
    this.carouselStates.forEach((state) => {
      this.goToSlide(state, 0, 'auto');
      this.startCarousel(state);
    });
    this.queueLayout();
  };

  onVisibilityChange = () => {
    this.carouselStates.forEach((state) => {
      if (document.hidden) this.stopCarousel(state);
      else this.startCarousel(state);
    });
  };

  onBlockSelect = (event) => {
    this.queueLayout();
    if (!this.mobileQuery.matches) return;
    const slide = event.target.closest?.('[data-pmp-slide]');
    if (!slide) return;
    const state = this.carouselStates.find((item) => item.track.contains(slide));
    if (!state) return;
    this.goToSlide(state, state.slides.indexOf(slide));
    this.restartCarousel(state);
  };

  setupConnections() {
    this.connectionSvg = this.querySelector('[data-pmp-connections]');
    this.connectionLayer = this.querySelector('[data-pmp-connection-layer]');
    this.connectionStage = this.querySelector('.pmp__stage');
    this.connectionHeader = this.querySelector('.pmp__header');
    this.connectionMedia = this.querySelector('.pmp__media');
    this.connectionSteps = Array.from(this.querySelectorAll('.pmp__step'));
    if (!this.connectionSvg || !this.connectionLayer || !this.connectionStage) return;

    if ('ResizeObserver' in window) {
      this.connectionObserver = new ResizeObserver(this.queueLayout);
      this.connectionObserver.observe(this.connectionStage);
      if (this.connectionHeader) this.connectionObserver.observe(this.connectionHeader);
      if (this.connectionMedia) this.connectionObserver.observe(this.connectionMedia);
      this.connectionSteps.forEach((step) => this.connectionObserver.observe(step));
    }

    window.addEventListener('resize', this.queueLayout, { passive: true });
    document.fonts?.ready.then(this.queueLayout);
    this.queueLayout();
  }

  updateHeaderOffset() {
    if (!this.connectionStage || !this.connectionHeader) return;
    if (this.mobileQuery.matches) {
      this.style.removeProperty('--pmp-header-offset');
      return;
    }

    const headerTop = Number.parseFloat(getComputedStyle(this.connectionHeader).top) || 0;
    const firstCardTop = 312;
    const safeGap = 30;
    const offset = Math.max(0, Math.ceil(headerTop + this.connectionHeader.offsetHeight + safeGap - firstCardTop));
    this.style.setProperty('--pmp-header-offset', `${offset}px`);
  }

  queueLayout = () => {
    this.updateHeaderOffset();
    this.queueConnections();
  };

  queueConnections = () => {
    cancelAnimationFrame(this.connectionFrame);
    this.connectionFrame = requestAnimationFrame(() => this.drawConnections());
  };

  drawConnections() {
    if (!this.connectionSvg || !this.connectionLayer || !this.connectionStage) return;
    this.connectionLayer.replaceChildren();
    this.connectionSvg.classList.remove('has-routes');
    if (this.mobileQuery.matches || this.connectionSteps.length === 0) return;

    const stageRect = this.connectionStage.getBoundingClientRect();
    if (stageRect.width === 0 || stageRect.height === 0) return;
    this.connectionSvg.setAttribute('viewBox', `0 0 ${stageRect.width} ${stageRect.height}`);
    this.connectionSvg.setAttribute('preserveAspectRatio', 'none');

    const box = (element) => {
      const rect = element.getBoundingClientRect();
      return {
        left: rect.left - stageRect.left,
        right: rect.right - stageRect.left,
        top: rect.top - stageRect.top,
        bottom: rect.bottom - stageRect.top,
        width: rect.width,
        height: rect.height,
      };
    };

    const steps = this.connectionSteps.map(box);
    const media = this.connectionMedia ? box(this.connectionMedia) : null;
    const routes = [];

    if (steps[0] && media) {
      const start = { x: steps[0].right + 8, y: steps[0].top + steps[0].height * 0.52 };
      const mediaRadius = this.connectionMedia.offsetWidth / 2;
      const mediaCenterX = this.connectionMedia.offsetLeft;
      const mediaCenterY = this.connectionMedia.offsetTop + this.connectionMedia.offsetHeight / 2;
      const end = { x: mediaCenterX - mediaRadius - 2, y: mediaCenterY };
      const distance = Math.max(28, end.x - start.x);
      const waveHeight = Math.min(46, Math.max(24, distance * 0.18));
      routes.push({
        start,
        end,
        c1: { x: start.x + distance * 0.36, y: start.y + waveHeight },
        c2: { x: end.x - distance * 0.3, y: end.y - waveHeight },
      });
    }

    if (steps[1] && steps[2]) {
      const start = { x: steps[1].right + 8, y: steps[1].bottom - 22 };
      const end = { x: steps[2].left - 10, y: steps[2].bottom - 18 };
      routes.push({
        start,
        end,
        c1: { x: start.x + 68, y: start.y + 58 },
        c2: { x: end.x - 72, y: end.y + 22 },
      });
    }

    if (steps[2] && steps[3]) {
      const start = { x: steps[2].right + 10, y: steps[2].bottom - 26 };
      const end = { x: steps[3].left - 10, y: steps[3].bottom - 25 };
      routes.push({
        start,
        end,
        c1: { x: start.x + 82, y: start.y + 34 },
        c2: { x: end.x - 78, y: end.y + 34 },
      });
    }

    if (steps[3] && steps[4]) {
      const start = { x: steps[3].right - 3, y: steps[3].top + 31 };
      const end = { x: steps[4].right - 3, y: steps[4].bottom - 27 };
      const outerX = Math.min(stageRect.width - 5, Math.max(start.x, end.x) + 34);
      routes.push({
        start,
        end,
        c1: { x: outerX, y: start.y - 54 },
        c2: { x: outerX, y: end.y + 62 },
      });
    }

    routes.forEach((route, index) => this.renderConnection(route, index));
    this.connectionSvg.classList.toggle('has-routes', routes.length > 0);
  }

  renderConnection(route, index) {
    const svgNamespace = 'http://www.w3.org/2000/svg';
    const pathData = `M ${route.start.x} ${route.start.y} C ${route.c1.x} ${route.c1.y}, ${route.c2.x} ${route.c2.y}, ${route.end.x} ${route.end.y}`;

    const underlay = document.createElementNS(svgNamespace, 'path');
    underlay.setAttribute('d', pathData);
    underlay.setAttribute('class', 'pmp__connection-underlay');
    underlay.setAttribute('vector-effect', 'non-scaling-stroke');
    this.connectionLayer.append(underlay);

    const path = document.createElementNS(svgNamespace, 'path');
    path.setAttribute('d', pathData);
    path.setAttribute('class', 'pmp__connection-path');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    path.style.setProperty('--pmp-route-index', index);
    this.connectionLayer.append(path);

    [route.start, route.end].forEach((point) => {
      const dotHalo = document.createElementNS(svgNamespace, 'circle');
      dotHalo.setAttribute('cx', point.x);
      dotHalo.setAttribute('cy', point.y);
      dotHalo.setAttribute('r', '6.5');
      dotHalo.setAttribute('class', 'pmp__connection-dot-halo');
      dotHalo.setAttribute('vector-effect', 'non-scaling-stroke');
      this.connectionLayer.append(dotHalo);

      const dot = document.createElementNS(svgNamespace, 'circle');
      dot.setAttribute('cx', point.x);
      dot.setAttribute('cy', point.y);
      dot.setAttribute('r', '4.3');
      dot.setAttribute('class', 'pmp__connection-dot');
      dot.setAttribute('vector-effect', 'non-scaling-stroke');
      this.connectionLayer.append(dot);
    });
  }

  setupObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isInView = true;
      this.classList.add('is-revealed');
      this.carouselStates.forEach((state) => this.startCarousel(state));
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isInView = entry.isIntersecting;
        if (entry.isIntersecting) this.classList.add('is-revealed');
        this.carouselStates.forEach((state) => {
          if (entry.isIntersecting) this.startCarousel(state);
          else this.stopCarousel(state);
        });
      },
      { rootMargin: '100px 0px', threshold: 0.08 }
    );
    this.observer.observe(this);
  }
}

if (!customElements.get('premium-making-process')) {
  customElements.define('premium-making-process', PremiumMakingProcess);
}
